import { AdminInsurerAttentionListTemplate } from '@/components/templates';
import { useAdminInsurerAttentionList } from '@/hooks/admin';
import { useRedirectWhenPageEmpty } from '@/hooks/useRedirectWhenPageEmpty';
import {
  AdminInsurerAttention,
  AdminInsurerAttentionFilterParams,
} from '@/types/admin-insurer-attention';
import { SorterResult } from 'antd/es/table/interface';
import { useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';

const SiteAdminInsurerAttentionPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();

  const [reflectRecord, setReflectRecord] = useState<AdminInsurerAttention | null>(
    (location.state as { reflectRecord?: AdminInsurerAttention } | null)?.reflectRecord ?? null
  );

  const page = searchParams.get('page') || '1';
  const sortBy = searchParams.get('sort_by') || undefined;
  const order = searchParams.get('order') as 'ascend' | 'descend' | null;
  const insurerNumber = searchParams.get('insurer_number') || undefined;
  const insurerName = searchParams.get('insurer_name') || undefined;
  const insurerNameKana = searchParams.get('insurer_name_kana') || undefined;
  const insurerTypeId = searchParams.get('insurer_type_id') || undefined;
  const prefectureId = searchParams.get('prefecture_id') || undefined;

  const breadcrumbItems = [{ title: '県別注意事項' }];

  const { data, isLoading, isError } = useAdminInsurerAttentionList({
    page,
    sort_by: sortBy,
    order: order || undefined,
    insurer_number: insurerNumber,
    insurer_name: insurerName,
    insurer_name_kana: insurerNameKana,
    insurer_type_id: insurerTypeId,
    prefecture_id: prefectureId,
  });

  const listData = data?.data || [];

  useRedirectWhenPageEmpty({
    searchParams,
    setSearchParams,
    isLoading,
    isError,
    dataLength: listData.length,
  });

  const handlePageChange = (page: number) => {
    const params = new URLSearchParams(searchParams);
    params.set('page', page.toString());
    setSearchParams(params);
  };

  const handleSearch = (filters: AdminInsurerAttentionFilterParams) => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value) params.set(key, value.toString());
    });
    params.set('page', '1');
    setSearchParams(params);
  };

  const handleBulkCopySuccess = () => {
    const params = new URLSearchParams(searchParams);
    params.set('page', '1');
    setSearchParams(params);
  };

  const handleDeleteSuccess = () => {
    const params = new URLSearchParams(searchParams);
    params.set('page', '1');
    setSearchParams(params);
  };

  const handleTableChange = (
    sorter: SorterResult<AdminInsurerAttention> | SorterResult<AdminInsurerAttention>[]
  ) => {
    const params = new URLSearchParams(searchParams);
    const singleSorter = Array.isArray(sorter) ? sorter[0] : sorter;

    if (singleSorter.order) {
      params.set('sort_by', (singleSorter.field || singleSorter.columnKey) as string);
      params.set('order', singleSorter.order);
    } else {
      params.delete('sort_by');
      params.delete('order');
    }
    params.set('page', '1');
    setSearchParams(params);
  };

  return (
    <AdminInsurerAttentionListTemplate
      data={listData}
      total={data?.meta.total || 0}
      current={data?.meta.current_page || 1}
      pageSize={data?.meta.per_page || 100}
      sortInfo={{
        columnKey: sortBy,
        order: order || undefined,
      }}
      onChange={handlePageChange}
      onTableChange={handleTableChange}
      onSearch={handleSearch}
      onBulkCopySuccess={handleBulkCopySuccess}
      onDeleteSuccess={handleDeleteSuccess}
      breadcrumbItems={breadcrumbItems}
      isLoading={isLoading}
      reflectRecord={reflectRecord}
      onReflect={setReflectRecord}
    />
  );
};

export default SiteAdminInsurerAttentionPage;
